import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Form,
  Space,
  Button,
  Drawer,
} from '@lockerpm/design';

import cipherFormItemComponents from '../../../../web-sh/src/components/cipher/form-item';
import foldersComponents from '../../../../web-sh/src/pages/admin/folders/components';
import cipherFormComponents from '../../../../components/cipher/form';
import modalsComponents from '../../../../components/modals';

import { CipherType } from '../../../../web-sh/src/core-js/src/enums';

import cipherServices from '../../../../web-sh/src/services/cipher';

import global from '../../../../config/global';
import common from '../../../../utils/common';
import storeActions from '../../../../store/actions';

function FormData(props) {
  const {
    ItemName,
    Notes,
    SelectFolder,
    CustomFields,
    PasswordOTP,
    Attachments,
    AlertWarning
  } = cipherFormItemComponents;
  const {
    PasswordForm,
    CardForm,
    CryptoBackupForm,
    IdentityForm,
  } = cipherFormComponents;

  const {
    PasswordViolatedModal
  } = modalsComponents;

  const FolderFormData = foldersComponents.FormData

  const {
    visible = false,
    item = null,
    cipherType = {},
    cloneMode = false,
    folderId = null,
    isTutorial = false,
    setCloneMode = () => {},
    onClose = () => {},
  } = props;

  const { t } = useTranslation()

  const allCiphers = useSelector((state) => state.cipher.allCiphers);
  const allCollections = useSelector((state) => state.collection.allCollections);
  const tutorialRunning = useSelector((state) => state.onboarding.tutorialRunning);
  const stepIndex = useSelector((state) => state.onboarding.stepIndex);
  const tutorialDetailRunning = useSelector((state) => state.onboarding.tutorialDetailRunning);
  const plan = useSelector((state) => state.payment.plan);
  const planLimit = useSelector((state) => state.payment.planLimit);
  const selectedAlias = useSelector((state) => state.privateEmail.selectedAlias)

  const [form] = Form.useForm()
  const [callingAPI, setCallingAPI] = useState(false);
  const [folderVisible, setFolderVisible] = useState(false);
  const [violatedVisible, setViolatedVisible] = useState(false);
  const [isCreateOtp, setIsCreateOtp] = useState(false);
  const [policyItem, setPolicyItem] = useState({ violations: [] });
  const [originCipher, setOriginCipher] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isUpload, setIsUpload] = useState(false);
  const [fido2Credentials, setFido2Credentials] = useState([])
  const [totp, setTotp] = useState(null);

  const cipherTypes = global.constants.CIPHER_TYPES.filter((t) => t.isCreate)
  const type = Form.useWatch('type', form) || cipherType.type

  const originItem = useMemo(() => {
    return allCiphers.find((d) => d.id === item?.id)
  }, [allCiphers, item])

  const isUploading = useMemo(() => {
    return !!attachments.find((attach) => !attach.url)
  }, [attachments])

  const showAttachments = useMemo(() => {
    if (originItem?.id) {
      return common.isOwner(originItem) || originItem?.attachments?.length > 0
    }
    return !common.isFreePlan() || common.isEnterpriseMember()
  }, [originItem, plan, planLimit])

  useEffect(() => {
    if (visible && tutorialRunning && stepIndex === 0) {
      global.store.dispatch(storeActions.updateTutorialRunning(false));
      setTimeout(() => {
        global.store.dispatch(storeActions.updateTutorialDetailRunning(true));
      }, 600);
    }
  }, [visible, tutorialRunning, stepIndex])

  useEffect(() => {
    setIsUpload(false);
    if (visible) {
      form.resetFields();
      if (originItem?.id) {
        const formData = common.convertCipherToForm(originItem)
        form.setFieldsValue(formData)
        setOriginCipher(originItem);
        setAttachments(originItem?.attachments || []);
        setFido2Credentials(originItem?.login?.fido2Credentials || [])
        setTotp(originItem.login?.totp)
      } else {
        const formData = common.convertCipherToForm({
          ...(item || {}),
          type: cipherType.type || cipherTypes[0].type,
          folderId: folderId || '',
        })
        if (cipherType.type === CipherType.Login && selectedAlias) {
          formData['username'] = selectedAlias;
        }
        form.setFieldsValue(formData)
        setOriginCipher(null);
        setAttachments([]);
        setFido2Credentials([]);
        setTotp(null)
      }
    } else {
      setCloneMode(false);
      setCallingAPI(false);
    }
  }, [originItem, visible, cipherType, selectedAlias]);

  const otpLimited = useMemo(() => {
    const isFreePlan = common.isFreePlan();
    const isEnterpriseMember = common.isEnterpriseMember();
    if (isFreePlan && planLimit && planLimit?.ciphers && planLimit?.plan_limit && !isEnterpriseMember) {
      return planLimit?.ciphers[CipherType.TOTP] >= planLimit?.plan_limit[CipherType.TOTP]
    }
    return false;
  }, [plan, planLimit])

  const handleTutorialRunning = async (isSave = false) => {
    if (tutorialDetailRunning && stepIndex === 0) {
      if (isSave) {
        await common.updateOnboardingProcess('add_pw');
      }
      global.store.dispatch(storeActions.updateTutorialDetailRunning(false))
      global.store.dispatch(storeActions.updateStepDetailIndex(0));
      global.navigate(global.keys.SETTINGS_IMPORT_EXPORT);
      global.store.dispatch(storeActions.updateStepIndex(1));
      setTimeout(() => {
        global.store.dispatch(storeActions.updateTutorialRunning(true))
      }, 100)
    }
  }

  const checkingPolicyItem = (callback = () => {}) => {
    form.validateFields().then((values) => {
      let policyInvalid = false;
      if (type === CipherType.Login) {
        const violations = common.checkPasswordPolicy(values.password);
        if (violations.length > 0) {
          setPolicyItem({ violations });
          setViolatedVisible(true);
          policyInvalid = true;
        }
      }
      if (!policyInvalid) {
        callback()
      }
    })
  }

  const handleCloseFormData = () => {
    setCallingAPI(false);
    setViolatedVisible(false);
    onClose()
    handleTutorialRunning(false);
    global.store.dispatch(storeActions.updateSelectedAlias(null))
  }

  const handleSave = async () => {
    form.validateFields().then(async (values) => {
      setCallingAPI(true);
      if (cloneMode || !originItem?.id) {
        await createCipher(values);
      } else {
        if (type === CipherType.Login) {
          await editCipher({
            ...values,
            totp,
            fido2Credentials
          });
        } else {
          await editCipher(values)
        }
      }
      setPolicyItem({ violations: [] });
      if (isUploading) {
        setIsUpload(true);
      } else {
        handleCloseFormData();
      }
    })
  }

  const createCipher = async (values) => {
    const cipher = common.convertFormToCipher({ ...values, type: type });
    const passwordStrength = values.password ? common.getPasswordStrength(values.password) : {};
    const { data, collectionIds } = await common.getEncCipherForRequest(
      cipher,
      {
        writeableCollections: allCollections.filter((c) => common.isChangeCipher(c)),
        isNewCipher: true,
        cloneMode
      }
    )
    const payload = {
      ...data,
      collectionIds,
      score: passwordStrength.score,
    }
    await cipherServices.create(payload).then(async (response) => {
      setOriginCipher({ ...cipher, id: response.id })
      await createOtp(values);
      global.pushSuccess(t('notification.success.cipher.created'));
      await handleTutorialRunning(true);
    }).catch((error) => {
      global.pushError(error)
    })
  }

  const editCipher = async (values) => {
    const oldValues = common.convertCipherToForm(originItem)
    const cipher = {
      ...common.convertFormToCipher({ ...oldValues, ...values, type: type }),
      id: originItem.id,
      attachments: originItem.attachments,
      passwordHistory: originItem.passwordHistory,
      organizationId: originItem.organizationId,
      favorite: originCipher.favorite,
    };
    const passwordStrength = values.password ? common.getPasswordStrength(values.password) : {};
    const { data, collectionIds } = await common.getEncCipherForRequest(
      cipher,
      {
        writeableCollections: allCollections.filter((c) => common.isChangeCipher(c)),
        nonWriteableCollections: allCollections.filter((c) => !common.isChangeCipher(c)),
      }
    )
    const payload = {
      ...data,
      collectionIds,
      score: passwordStrength.score,
    }
    await cipherServices.update(originItem.id, payload).then(async () => {
      setOriginCipher(cipher)
      await createOtp(values);
      global.pushSuccess(t('notification.success.cipher.updated'))
    }).catch((error) => {
      global.pushError(error)
    })
  }

  const createOtp = async (values) => {
    if (isCreateOtp && values.totp && !otpLimited) {
      const otp = { notes: values.totp, name: values.name };
      const cipher = common.convertFormToCipher({ ...otp, type: CipherType.TOTP });
      const { data } = await common.getEncCipherForRequest( cipher )
      const payload = { ...data, collectionIds: [] }
      await cipherServices.create(payload)
    }
  }

  return (
    <div className={props.className}>
      <Drawer
        className="vault-form-drawer"
        title={t( `inventory.${cipherType.key}.${originItem && !cloneMode ? 'edit' : 'add'}`)}
        placement="right"
        onClose={() => handleCloseFormData()}
        open={visible}
        closable={!isTutorial}
        width={600}
        footer={
          <Space className='flex items-center justify-end'>
            <Button
              className="vault-form-close-btn"
              disabled={callingAPI}
              onClick={() => handleCloseFormData()}
            >
              {t('button.cancel')}
            </Button>
            <Button
              className="vault-form-save-btn"
              type="primary"
              loading={callingAPI}
              onClick={() => checkingPolicyItem(handleSave)}
            >
            { t('button.save') } 
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          labelAlign={'left'}
          className='vault-form flex flex-col gap-4'
        >
          <AlertWarning
            folderId={folderId}
          />
          <ItemName
            item={originItem}
            cipherTypes={cipherTypes}
            cipherType={cipherType}
            disabled={callingAPI}
            isTutorial={isTutorial}
            onChange={(v) => {
              const formData = common.convertCipherToForm({
                type: v,
                folderId: folderId || '',
              })
              form.setFieldsValue(formData)
            }}
          />
          {
            type === CipherType.Login && <PasswordForm
              visible={visible}
              form={form}
              disabled={callingAPI}
              fido2Credentials={fido2Credentials}
              setFido2Credentials={setFido2Credentials}
            />
          }
          {
            type === CipherType.Card && <CardForm
              form={form}
              disabled={callingAPI}
            />
          }
          {
            type === CipherType.CryptoWallet && <CryptoBackupForm
              form={form}
              disabled={callingAPI}
            />
          }
          {
            type === CipherType.Identity && <IdentityForm
              form={form}
              disabled={callingAPI}
            />
          }
          {
            type === CipherType.Login && <PasswordOTP
              item={originItem}
              totp={totp}
              visible={visible}
              disabled={callingAPI}
              otpLimited={otpLimited}
              setTotp={setTotp}
              setIsCreateOtp={setIsCreateOtp}
            />
          }            
          <Notes
            disabled={callingAPI}
          />
          <CustomFields
            form={form}
            disabled={callingAPI}
          />
          {
            showAttachments && <div className={'mb-4'}>
              <p className='font-semibold mb-1'>{t('attachments.title')}</p>
              <Attachments
                isUpload={isUpload}
                originCipher={originCipher}
                isUploading={isUploading}
                isCipherForm={true}
                callingAPI={callingAPI}
                attachments={attachments}
                setCallingAPI={setCallingAPI}
                setAttachments={setAttachments}
                uploaded={handleCloseFormData}
              />
            </div>
          }
          <SelectFolder
            form={form}
            disabled={callingAPI || !!folderId}
            item={originItem}
            placement="topLeft"
            onCreate={() => setFolderVisible(true)}
          />
        </Form>
      </Drawer>
      <FolderFormData
        visible={folderVisible}
        onClose={() => setFolderVisible(false)}
        callback={(res) => {
          setTimeout(() => {
            form.setFieldValue('folderId', res.id)
          }, 2000);
        }}
      />
      <PasswordViolatedModal
        visible={violatedVisible}
        item={policyItem}
        callingAPI={callingAPI}
        isForm={true}
        onOk={() => handleSave()}
        onClose={() => setViolatedVisible(false)}
      />
    </div>
  );
}

export default FormData;
